import { css } from '@emotion/react';
import { theme } from '@frontend/theme';
import { MultiStep, MultiStepControl, Stepper, useMultiStepContext } from '@frontend/design-system';

export type RegisterTerminalStepperProps = {
  steps: {
    id: string;
    title: string;
    stepperLabel: string;
    component: React.ReactNode;
    parentId?: string;
    isComplete?: boolean;
  }[];
  multiStepControl: MultiStepControl;
};

const styles = {
  overrides: css`
    padding: ${theme.spacing(2)};
    padding-right: ${theme.spacing(3)};

    .stepper__label-text {
      overflow: visible;
      text-overflow: unset;
      white-space: normal;
    }

    .wv-modal-flex-content {
      padding-left: 0;
      padding-right: 0;
    }

    .multi-step__header-wrapper {
      padding-top: ${theme.spacing(1)};
    }
    .multi-step__footer-wrapper {
      display: none;
    }
    .register-terminal__footer {
      padding-left: 0;
      padding-right: 0;
      margin-top: auto;
    }
    .step__body {
      display: flex;
      flex-direction: column;
    }
    .multi-step__body {
      padding-left: ${theme.spacing(3)};
      padding-bottom: ${theme.spacing(1)};
    }
  `,
  stepper: css`
    min-width: 180px;
    padding-left: ${theme.spacing(2)};
    border-right: 1px solid ${theme.colors.neutral20};
  `,
  stepperCard: css`
    border: none;
    padding: ${theme.spacing(1)};
    align-items: center;
    display: flex;
    margin-bottom: 0px;
    margin-left: ${theme.spacing(3)} !important;
  `,
  stepperTitle: css`
    & h2.step-content-title {
      font-size: ${theme.font.size.large};
      width: 100%;
      white-space: normal;
      overflow: visible;
      height: auto;
    }
  `,
};

type CustomStepperProps = {
  steps: RegisterTerminalStepperProps['steps'];
};

const CustomStepper = ({ steps }: CustomStepperProps) => {
  const { activeStep } = useMultiStepContext();

  // Get the active step's label
  const activeStepLabel = steps.find((step) => step.id === activeStep)?.stepperLabel;

  // Group steps by label and get unique steps
  const uniqueSteps = steps.reduce<Record<string, (typeof steps)[0]>>((acc, step) => {
    if (!acc[step.stepperLabel]) {
      acc[step.stepperLabel] = step;
    }
    return acc;
  }, {});

  // Calculate step status for unique steps
  const stepStatus = Object.values(uniqueSteps).reduce<Record<number, 'completed' | 'active' | 'inactive'>>(
    (acc, step, index) => {
      const isActive = step.stepperLabel === activeStepLabel;
      const isCompleted = step.isComplete;
      return {
        ...acc,
        [index + 1]: isActive ? 'active' : isCompleted ? 'completed' : 'inactive',
      };
    },
    {}
  );

  return (
    <Stepper css={styles.stepper} stepStatus={stepStatus}>
      {Object.values(uniqueSteps).map((step) => (
        <Stepper.Card css={styles.stepperCard} key={step.id} preventDefaultOnClick>
          <Stepper.Title css={styles.stepperTitle}>{step.stepperLabel}</Stepper.Title>
        </Stepper.Card>
      ))}
    </Stepper>
  );
};

export const RegisterTerminalStepper = ({ steps, multiStepControl }: RegisterTerminalStepperProps) => {
  return (
    <MultiStep.Root
      {...multiStepControl}
      css={styles.overrides}
      components={{ Stepper: () => <CustomStepper steps={steps} /> }}
    >
      {steps.map((step) => (
        <MultiStep.Step
          components={{ Footer: () => null }}
          label={step.title}
          id={step.id}
          key={step.id}
          parentId={step.parentId}
        >
          {step.component}
        </MultiStep.Step>
      ))}
    </MultiStep.Root>
  );
};
